/* A missing deployment chunk shows an explicit recovery action. Never reload
 * automatically: effects may already have unmounted the editor by the time
 * componentDidCatch runs, so absence in the unsaved-work registry is not proof
 * that discarding the page is safe. Draft cleanup saves recoverable state; the
 * button still warns about local files/storage failure and waits for uploads.
 * Non-chunk errors continue to propagate. */
import { Component } from "react";
import { deferWhileUploading } from "../lib/uploadsInFlight";
import { unsavedWorkState } from "../lib/unsavedWork";

const RELOAD_FLAG = "orca:chunk-reload";

// Los navegadores no comparten un tipo de error para esto. Chrome/Edge lanzan
// `ChunkLoadError`; Firefox y Safari, un TypeError de import dinámico. Se
// reconocen por nombre y por texto, y se exporta para poder medirlo en un test
// sin montar React.
export function isChunkLoadError(error) {
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  return name === "ChunkLoadError"
    || /Loading chunk [^\s]+ failed/i.test(message)
    || /Loading CSS chunk/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);
}

export default class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, unsaved: null, waiting: false };
  }

  static getDerivedStateFromError(error) {
    if (!isChunkLoadError(error)) throw error;   // no es nuestro caso: que suba
    return { failed: true };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;
    let unsaved = null;
    try { unsaved = unsavedWorkState(); } catch (_) { /* explicit action regardless */ }
    this.setState({ unsaved });

  }

  render() {
    if (!this.state.failed) return this.props.children;
    const { unsaved } = this.state;
    const localFiles = (unsaved && unsaved.localFiles) || [];
    let pending = null;
    if (unsaved && unsaved.dirty) {
      pending = localFiles.length
        ? `Hay archivos locales sin subir que NO se pueden recuperar: ${localFiles.join(", ")}. Tendrás que volver a elegirlos.`
        : unsaved.storageFailed
          ? "No se pudo guardar el borrador en este navegador: lo que no hayas enviado se perderá al recargar."
          : "Tu trabajo sin enviar se guardó como borrador y reaparecerá al recargar.";
    }
    return (
      <div
        data-testid="chunk-error-fallback"
        style={{
          minHeight: "100vh", background: "#09090b", color: "#E6E6EE",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 14, fontSize: 14, padding: 24,
          textAlign: "center",
        }}
      >
        <span style={{ fontSize: 22 }}>🐋</span>
        <span>Orca Deck se ha actualizado y esta pestaña se quedó con la versión anterior.</span>
        {pending && <span data-testid="chunk-error-unsaved" style={{ maxWidth: 520, color: "#FBBF24" }}>{pending}</span>}
        <button
          data-testid="chunk-error-reload"
          disabled={this.state.waiting}
          onClick={() => {
            if ((localFiles.length || unsaved?.storageFailed) && typeof window.confirm === "function"
              && !window.confirm(`${pending}\n\n¿Recargar de todos modos?`)) return;
            this.setState({ waiting: true });
            deferWhileUploading(() => {
              try { sessionStorage.removeItem(RELOAD_FLAG); } catch (_) { /* ignore */ }
              if (this.props.onReload) this.props.onReload(); else window.location.reload();
            }, { ceilingMs: Infinity });
          }}
          style={{
            background: "#4F46E5", color: "#fff", border: "none", borderRadius: 8,
            padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >{this.state.waiting ? "Esperando la subida…" : "Recargar"}</button>
      </div>
    );
  }
}

// Una carga completa y sana borra la marca: si no, el PRÓXIMO deploy encontraría
// la bandera puesta de un incidente viejo y enseñaría el mensaje sin intentar
// siquiera la recarga que casi siempre lo resuelve.
export function clearChunkReloadFlag() {
  try { sessionStorage.removeItem(RELOAD_FLAG); } catch (_) { /* ignore */ }
}
